/**
 * Revenue Analytics API
 * Reference: TAX_SYSTEM_IMPLEMENTATION.md - Section 5.7 Revenue Analytics Routes
 */
import { apiClient } from './apiClient';

export interface RevenueAnalytics {
  totalRevenue: number;
  totalSales: number;
  totalCosts: number;
  profitMargin: number;
}

export interface RevenueByProduct {
  productId: number;
  productName: string;
  quantitySold: number;
  revenue: number;
  profitMargin: number;
}

export interface RevenueBySalesPoint {
  salesPointId: number;
  salesPointName: string;
  revenue: number;
  profitMargin: number;
  totalSales: number;
  totalCosts: number;
}

export interface RevenueByCategory {
  categoryId: number;
  categoryName: string;
  totalQuantitySold: number;
  totalRevenue: number;
  totalSales: number;
  totalCosts: number;
  profitMargin: number;
}

export interface RevenueTimeSeriesPoint {
  date: string;
  revenue: number;
  sales: number;
  costs: number;
}

export interface CostCompletenessValidation {
  totalProducts: number;
  productsWithCost: number;
  productsWithoutCost: number;
  completenessPercentage: number;
  missingCostProducts: Array<{
    productId: number;
    productName: string;
  }>;
}

export interface ProductStatistics {
  productId: number;
  productName: string;
  totalQuantitySold: number;
  totalRevenue: number;
  totalSales: number;
  totalCosts: number;
  profitMargin: number;
  averageRevenuePerUnit: number;
}

export interface SalesPointStatistics {
  salesPointId: number;
  salesPointName: string;
  totalRevenue: number;
  totalSales: number;
  totalCosts: number;
  profitMargin: number;
  totalTransactions: number;
  averageTransactionValue: number;
}

export interface RevenueAnalyticsFilters {
  from?: string;
  to?: string;
  salesPointId?: number;
  productId?: number;
  /** S3-T06 — ALL omitted on wire; CASH | QR forwarded as paymentMethod. */
  paymentMethod?: 'CASH' | 'QR';
  /**
   * Local IANA zone for stats windows (T15) — e.g. Europe/Prague from browser.
   * BAN UTC-only month bounds on the FE caller.
   */
  timezone?: string;
}

function appendRevenueAnalyticsQueryParams(
  params: URLSearchParams,
  filters?: RevenueAnalyticsFilters,
): void {
  if (filters?.from) {
    params.append('from', filters.from);
  }
  if (filters?.to) {
    params.append('to', filters.to);
  }
  if (filters?.salesPointId !== undefined) {
    params.append('salesPointId', filters.salesPointId.toString());
  }
  if (filters?.productId !== undefined) {
    params.append('productId', filters.productId.toString());
  }
  if (filters?.paymentMethod) {
    params.append('paymentMethod', filters.paymentMethod);
  }
  if (filters?.timezone !== undefined && filters.timezone.trim().length > 0) {
    params.append('timezone', filters.timezone.trim());
  }
}

export const revenueAnalyticsApi = {
  getRevenueAnalytics: (filters?: RevenueAnalyticsFilters) => {
    const params = new URLSearchParams();
    appendRevenueAnalyticsQueryParams(params, filters);
    const query = params.toString();
    return apiClient.get<{ analytics: RevenueAnalytics }>(
      `/api/v1/admin/analytics/revenue${query ? `?${query}` : ''}`
    );
  },

  getRevenueByProduct: (filters?: RevenueAnalyticsFilters) => {
    const params = new URLSearchParams();
    appendRevenueAnalyticsQueryParams(params, filters);
    const query = params.toString();
    return apiClient.get<{ products: RevenueByProduct[] }>(
      `/api/v1/admin/analytics/revenue/by-product${query ? `?${query}` : ''}`
    );
  },

  getRevenueBySalesPoint: (filters?: RevenueAnalyticsFilters) => {
    const params = new URLSearchParams();
    appendRevenueAnalyticsQueryParams(params, filters);
    const query = params.toString();
    return apiClient.get<{ salesPoints: RevenueBySalesPoint[] }>(
      `/api/v1/admin/analytics/revenue/by-sales-point${query ? `?${query}` : ''}`
    );
  },

  getRevenueByCategory: (filters?: RevenueAnalyticsFilters) => {
    const params = new URLSearchParams();
    appendRevenueAnalyticsQueryParams(params, filters);
    const query = params.toString();
    return apiClient.get<{ categories: RevenueByCategory[] }>(
      `/api/v1/admin/analytics/revenue/by-category${query ? `?${query}` : ''}`
    );
  },

  getRevenueTimeSeries: (filters: RevenueAnalyticsFilters & { interval: 'daily' | 'weekly' | 'monthly' }) => {
    const params = new URLSearchParams();
    appendRevenueAnalyticsQueryParams(params, filters);
    params.append('interval', filters.interval);
    return apiClient.get<{ timeSeries: RevenueTimeSeriesPoint[] }>(
      `/api/v1/admin/analytics/revenue/time-series?${params.toString()}`
    );
  },

  validateCostCompleteness: () =>
    apiClient.get<{ validation: CostCompletenessValidation }>('/api/v1/admin/analytics/revenue/cost-completeness'),

  getProductStatistics: (productId: number, filters?: RevenueAnalyticsFilters) => {
    const params = new URLSearchParams();
    appendRevenueAnalyticsQueryParams(params, filters);
    const query = params.toString();
    return apiClient.get<{ statistics: ProductStatistics }>(
      `/api/v1/admin/products/${productId}/statistics${query ? `?${query}` : ''}`
    );
  },

  getSalesPointStatistics: (salesPointId: number, filters?: RevenueAnalyticsFilters) => {
    const params = new URLSearchParams();
    appendRevenueAnalyticsQueryParams(params, filters);
    const query = params.toString();
    return apiClient.get<{ statistics: SalesPointStatistics }>(
      `/api/v1/admin/sales-points/${salesPointId}/statistics${query ? `?${query}` : ''}`
    );
  },
};

